import FilterableArticleGrid from '@/components/blog/FilterableArticleGrid';
import SearchForm from '@/components/blog/SearchForm';
import { FilterBlock_Category } from '@/components/FilterBlock';
import { Container } from '@/components/layout/Container';
import { GlobalContext } from '@/context';
import { BlogSearchResultQuery, BlogSearchResultQueryVariables } from '@/graphql';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPageProps } from '@/lib/next';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import BlogTemplate from '@/templates/BlogTemplate';
import axios, { AxiosResponse } from 'axios';
import { capitalize, debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const ARTICLES_PER_PAGE = 12;

const SearchPage = styled.div`
  background: ${COLORS.WHITE};
  min-height: ${remCalc(540)};
  padding-top: ${remCalc(170)};

  ${mediaBreakpointUp('desktop')} {
  }
`;

const StyledSearchForm = styled(SearchForm)`
  && {
    width: calc(100% - ${remCalc(40)});
    margin: 0 auto ${remCalc(60)};

    ${mediaBreakpointUp('desktop')} {
      margin-bottom: ${remCalc(160)};
    }
  }
`;

const defaultResults = {
  entries: [],
  entryCount: 0,
};

interface BlogSearchProps { }

const BlogSearch: PageTemplate<BlogSearchProps> = (props) => {
  const { categories } = useContext(GlobalContext);
  const [, activeBreakpoints] = useBreakpoint();

  const [searchTerms, setSearchTerms] = useState('');
  const [activeFilterIds, setActiveFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [results, setResults] = useState<BlogSearchResultQuery>(defaultResults);

  const [searchCategories, cacheKeyParams] = useMemo(() => {
    const filters: { id?: string[] } = {};

    if (activeFilterIds.length) {
      filters.id = activeFilterIds;
    }

    const cacheKey = activeFilterIds.map((id) => `categoryId=${id}`).join('&');

    return [[filters], cacheKey];
  }, [activeFilterIds]);

  const [cacheKey, setCacheKey] = useState(`/blog/search/${currentPage}?query=${searchTerms}&${cacheKeyParams}`);

  const updateQuery = useCallback(
    debounce((currentPage, searchTerms, cacheKeyParams) => {
      setCacheKey(`/blog/search/${currentPage}?query=${searchTerms}&${cacheKeyParams}`);
    }, 1000),
    [],
  );

  useEffect(() => {
    updateQuery(currentPage, searchTerms, cacheKeyParams);
  }, [currentPage, searchTerms, cacheKeyParams]);

  useEffect(() => {
    axios
      .post<BlogSearchResultQueryVariables, AxiosResponse<BlogSearchResultQuery>>('/blog/search/paginate', {
        search: searchTerms,
        limit: ARTICLES_PER_PAGE,
        offset: (currentPage - 1) * ARTICLES_PER_PAGE,
        categories: searchCategories,
      })
      .then((response) => {
        setResults(response.data);
      })
      .catch((err) => {
        setResults(defaultResults);
      });
  }, [cacheKey]);

  const { entries, entryCount } = results;

  /* Generate the array of filters */
  const filterCategories = useMemo(() => {
    const sortedFilters = categories?.reduce<FilterBlock_Category[]>((groups, category) => {
      const { id, slug, groupHandle, title } = category!;
      const index = groups.findIndex((group) => group.slug === groupHandle);

      const filter = {
        id: id!,
        title: title!,
        slug: slug!,
      };

      const titleFilter = ['blog', 'teams', 'industries'];

      if (index > -1) {
        groups[index].filters.push(filter);
      } else {
        if (titleFilter.includes(groupHandle!)) {
          groups.push({
            title: capitalize(groupHandle!),
            slug: groupHandle!,
            filters: [filter],
          });
        }
      }

      return groups;
    }, []);

    return (
      sortedFilters?.map((group) => {
        if (group.slug === 'blog') {
          group.title = 'Categories';
        }

        return group;
      }) || []
    );
  }, [categories]);

  return (
    <SearchPage>
      <Container>
        <StyledSearchForm query={searchTerms} onQueryUpdate={setSearchTerms} />

        <FilterableArticleGrid
          entries={entries!}
          filtersCollapsible={!activeBreakpoints.includes('desktop')}
          filterCategories={filterCategories}
          filterSelections={activeFilterIds}
          totalEntries={entryCount || 0}
          entriesPerPage={ARTICLES_PER_PAGE}
          currentPage={currentPage}
          hrefBuilder={() => `/blog/search`}
          onFilterToggle={(category, filter, isSelected) => {
            if (isSelected) {
              setActiveFilters([...activeFilterIds, filter.id]);
            } else {
              setActiveFilters(activeFilterIds.filter((id) => id !== filter.id));
            }
          }}
          onPageChange={setCurrentPage}
        />
      </Container>
    </SearchPage>
  );
};

BlogSearch.template = (props) => <BlogTemplate className='mt-0' {...props} />;

export default BlogSearch;

export const getStaticProps = getStaticPageProps('blog/search');
